<template>
  <div>
    <b-alert
      v-if="partialError"
      variant="warning"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ partialError }}
      </div>
    </b-alert>
    <b-alert
      v-if="loadError"
      variant="danger"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ loadError }}
      </div>
    </b-alert>

    <b-card class="mb-1">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ช่วงเวลา"
            label-for="preset"
          >
            <b-form-select
              id="preset"
              v-model="preset"
              :options="presetOptions"
              :disabled="isLoading"
              @change="onPresetChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ตั้งแต่"
            label-for="from"
          >
            <b-form-input
              id="from"
              v-model="from"
              type="date"
              :disabled="isLoading"
              @change="onDateChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
        >
          <b-form-group
            label="ถึง"
            label-for="to"
          >
            <b-form-input
              id="to"
              v-model="to"
              type="date"
              :disabled="isLoading"
              @change="onDateChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="3"
          class="d-flex align-items-center mb-1 mb-md-0"
        >
          <b-button
            variant="outline-primary"
            :disabled="isLoading"
            @click="fetchData"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-50"
            />
            รีเฟรช
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!--
      Path explorer — กางทีละขั้นเหมือน path exploration ของ Google Analytics
      แต่ละคอลัมน์คือ "ก้าวถัดไป" ของเส้นทางที่เลือกไว้ทางซ้าย คลิกกล่องเพื่อกางต่อ
    -->
    <b-card class="mb-1">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <div>
          <h4 class="mb-25">
            เส้นทางผู้ใช้
          </h4>
          <!-- breadcrumb ของเส้นทางที่กางอยู่ คลิกเพื่อถอยกลับไปขั้นนั้น -->
          <div class="path-crumbs">
            <b-link
              class="crumb"
              :class="{ 'crumb-active': !prefix.length }"
              @click="goToDepth(0)"
            >
              ทั้งหมด
            </b-link>
            <span
              v-for="(step, i) in prefix"
              :key="i"
            >
              <span class="text-muted mx-25">›</span>
              <b-link
                class="crumb"
                :class="{ 'crumb-active': i === prefix.length - 1 }"
                :title="step"
                @click="goToDepth(i + 1)"
              >{{ shorten(step, 28) }}</b-link>
            </span>
          </div>
        </div>
        <b-button
          v-if="prefix.length"
          size="sm"
          variant="flat-secondary"
          @click="goToDepth(0)"
        >
          เริ่มใหม่
        </b-button>
      </div>

      <div class="path-explorer">
        <div
          v-for="(col, ci) in columns"
          :key="ci"
          class="path-col"
        >
          <div class="path-col-head">
            ขั้นที่ {{ ci + 1 }}
            <small
              v-if="col.total"
              class="text-muted d-block"
            >{{ col.total.toLocaleString('en-US') }} ครั้ง</small>
          </div>

          <div
            v-if="col.loading"
            class="text-center py-2"
          >
            <b-spinner small />
          </div>

          <template v-else>
            <div
              v-for="(node, ni) in col.rows"
              :key="ni"
              class="path-node"
              :class="[
                `path-node-${node.kind}`,
                { 'path-node-open': isOpen(ci, node) },
                { 'path-node-clickable': node.kind === 'page' },
              ]"
              @click="onNodeClick(ci, node)"
            >
              <!-- แถบสัดส่วนเทียบกับยอดรวมของคอลัมน์นั้น อ่านขนาดได้โดยไม่ต้องคิดเลข -->
              <div
                class="path-node-bar"
                :style="{ width: pct(node, col) + '%' }"
              />
              <div class="path-node-body">
                <div
                  class="path-node-label"
                  :title="nodeTitle(node)"
                >
                  {{ nodeLabel(node) }}
                </div>
                <div class="path-node-meta">
                  {{ Number(node.sessions).toLocaleString('en-US') }}
                  <span class="text-muted">({{ pct(node, col) }}%)</span>
                  <!--
                    เวลาที่อยู่บนหน้านั้น ใช้ค่ามัธยฐาน ไม่ใช่ค่าเฉลี่ย เพราะข้อมูลเบ้หนัก
                    (ดูหมายเหตุของ page_dwell ใน report.model.js) จุดเตือนขึ้นเมื่อหน้านั้น
                    มี 0 วินาทีเกินครึ่ง = SDK จับเวลาไม่ทันก่อนแท็บถูกปิด เชื่อตัวเลขไม่ได้
                  -->
                  <span
                    v-if="dwellOf(node)"
                    class="path-node-time"
                    :title="dwellTitle(node)"
                  >· {{ dwellOf(node).median_seconds }} วิ<span
                    v-if="dwellShaky(node)"
                    class="text-warning"
                  >*</span></span>
                </div>
              </div>
            </div>

            <div
              v-if="!col.rows.length"
              class="text-muted small py-1"
            >
              ไม่มีข้อมูล
            </div>
          </template>
        </div>
      </div>

      <small class="text-muted">
        คลิกกล่องเพื่อกางขั้นถัดไป · กล่อง <strong>ออกจากเว็บ</strong> คือคนที่จบการเข้าชมตรงนั้น ·
        <strong>อื่น ๆ</strong> คือหน้าที่เหลือรวมกัน (กางต่อไม่ได้) ·
        ตัวเลขวินาทีคือ<strong>มัธยฐาน</strong>ของเวลาที่อยู่บนหน้านั้น
        (เอาเมาส์ชี้เพื่อดูค่าเฉลี่ย/p75) เครื่องหมาย <span class="text-warning">*</span>
        แปลว่าหน้านั้นมีการรายงาน 0 วินาทีเกินครึ่ง ตัวเลขจึงเชื่อได้ไม่เต็มที่
      </small>
    </b-card>

    <b-row>
      <!--
        คู่หน้าที่ติดกัน — ใช้ตอบว่า "หน้านี้ส่งคนต่อไปไหนได้บ้าง" โดยไม่ต้องไล่จากต้นทาง
        ต่างจาก explorer ด้านบนที่ต้องเริ่มจากหน้าแรกเสมอ
      -->
      <b-col md="6">
        <b-card
          :title="transitionCardTitle"
          class="mb-1"
        >
          <b-table
            class="path-table"
            :items="transitions"
            :fields="transitionFields"
            :busy="isLoading || transitionLoading"
            :tbody-tr-class="transitionRowClass"
            show-empty
            :empty-text="fromPage ? 'หน้านี้ไม่มีใครกดต่อไปไหน' : 'ยังไม่มีข้อมูล'"
            responsive
            striped
            hover
            @row-clicked="onTransitionClicked"
          >
            <template #cell(from_page)="row">
              <span :title="row.item.from_page">{{ shorten(row.item.from_page) }}</span>
            </template>
            <template #cell(seconds)="row">
              <span :title="dwellTitle({ next_page: row.item.from_page })">
                {{ dwellOf({ next_page: row.item.from_page }) ? `${dwellOf({ next_page: row.item.from_page }).median_seconds} วิ` : '—' }}
              </span>
            </template>
            <template #cell(to_page)="row">
              <span :title="row.item.to_page">{{ shorten(row.item.to_page) }}</span>
            </template>
          </b-table>
          <div
            v-if="showPager(transitionPage, transitionTotalPages, transitionHasMore)"
            class="d-flex justify-content-center align-items-center"
          >
            <b-button
              v-if="hasMultiplePages(transitionPage, transitionTotalPages, transitionHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="transitionPage <= 1 || isLoading || transitionLoading"
              @click="goTransitionPage(transitionPage - 1)"
            >
              ก่อนหน้า
            </b-button>
            <span class="mx-1 text-muted">{{ pageLabel(transitionPage, transitionTotalPages, transitionTotal) }}</span>
            <b-button
              v-if="hasMultiplePages(transitionPage, transitionTotalPages, transitionHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="!hasNextPage(transitionPage, transitionTotalPages, transitionHasMore) || isLoading || transitionLoading"
              @click="goTransitionPage(transitionPage + 1)"
            >
              ถัดไป
            </b-button>
          </div>
          <div class="d-flex align-items-center flex-wrap">
            <small class="text-muted mr-1">คลิกแถวเพื่อดูว่าจากหน้านั้นไปไหนต่อ (กดซ้ำเพื่อยกเลิก)</small>
            <b-button
              v-if="fromPage"
              size="sm"
              variant="flat-secondary"
              @click="clearFromPage"
            >
              ← ดูทุกหน้า
            </b-button>
          </div>
        </b-card>
      </b-col>

      <!-- เส้นทางเต็มที่ซ้ำกันบ่อย — ดูรูปแบบการเดินจริงแบบเห็นทั้งเส้น -->
      <b-col md="6">
        <b-card
          title="เส้นทางที่พบบ่อย"
          class="mb-1"
        >
          <b-table
            :items="paths"
            :fields="pathFields"
            :busy="isLoading || pathLoading"
            show-empty
            empty-text="ยังไม่มีข้อมูล"
            responsive
            striped
          >
            <template #cell(path)="row">
              <div class="path-cell">
                <span
                  v-for="(step, i) in row.item.path.split(' > ')"
                  :key="i"
                >
                  <b-badge
                    variant="light-secondary"
                    :title="step"
                  >{{ shorten(step, 30) }}</b-badge>
                  <span
                    v-if="i < row.item.path.split(' > ').length - 1"
                    class="text-muted mx-25"
                  >→</span>
                </span>
                <!--
                  API ตัดเส้นทางที่ 12 ก้าวเพราะเพดาน GROUP_CONCAT ของ MySQL
                  แต่ steps เป็นจำนวนจริง จึงบอกได้ว่ายังมีต่ออีกกี่ก้าว
                -->
                <small
                  v-if="hiddenSteps(row.item)"
                  class="text-muted d-block mt-25"
                >… อีก {{ hiddenSteps(row.item) }} ก้าว (ทั้งหมด {{ row.item.steps }})</small>
              </div>
            </template>
          </b-table>
          <div
            v-if="showPager(pathPage, pathTotalPages, pathHasMore)"
            class="d-flex justify-content-center align-items-center"
          >
            <b-button
              v-if="hasMultiplePages(pathPage, pathTotalPages, pathHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="pathPage <= 1 || isLoading || pathLoading"
              @click="goPathPage(pathPage - 1)"
            >
              ก่อนหน้า
            </b-button>
            <span class="mx-1 text-muted">{{ pageLabel(pathPage, pathTotalPages, pathTotal) }}</span>
            <b-button
              v-if="hasMultiplePages(pathPage, pathTotalPages, pathHasMore)"
              size="sm"
              variant="outline-secondary"
              :disabled="!hasNextPage(pathPage, pathTotalPages, pathHasMore) || isLoading || pathLoading"
              @click="goPathPage(pathPage + 1)"
            >
              ถัดไป
            </b-button>
          </div>
          <small class="text-muted">
            นับเฉพาะครั้งที่เปิดมากกว่า 1 หน้า · หน้าเดิมที่กดซ้ำติดกันถูกยุบเป็นก้าวเดียว
          </small>
        </b-card>
      </b-col>
    </b-row>

    <b-alert
      variant="secondary"
      show
    >
      <div class="alert-body">
        <small>
          <strong>ข้อจำกัดที่ควรรู้:</strong>
          การ์ดห้องบนหน้าหน้าบ้านเปิดแท็บใหม่ด้วย <code>rel="noopener"</code>
          แท็บใหม่จึงถูกนับเป็นการเข้าเว็บครั้งใหม่ เส้นทางจะขาดตรงจุดที่คลิกจากหน้า listing ไปหน้าห้อง
          — ช่วงนั้นดูได้ที่หน้า <strong>ห้อง → ทางเข้า</strong> ซึ่งผูกข้อมูลด้วยวิธีอื่น
        </small>
      </div>
    </b-alert>
  </div>
</template>

<script>
/*
 * เส้นทางของผู้ใช้ — เข้ามาแล้วเดินต่อไปไหน
 *
 * ส่วนหลักคือ path explorer ที่กางทีละขั้นแบบ path exploration ของ Google Analytics
 * เรียก GET /analytics/v1/report?type=path_tree&prefix=[...] ทีละขั้น
 * ไม่ได้ดึงต้นไม้ทั้งก้อนมาไว้ในเครื่อง เพราะขั้นแรกอย่างเดียวก็มีกิ่งเป็นพัน
 *
 * ประกอบกับอีกสองตาราง: คู่หน้าที่ติดกัน (page_transitions) และเส้นทางเต็ม (session_paths)
 */
import {
  BCard, BTable, BBadge, BFormSelect, BFormGroup, BFormInput,
  BButton, BRow, BCol, BAlert, BSpinner, BLink,
} from 'bootstrap-vue'
import {
  analyticsHttp, apiToken, settleAll, rowsOf, loadErrorMessage, partialErrorMessage,
  pageLabel, hasNextPage, showPager, hasMultiplePages,
} from './analyticsApi'

const iso = d => d.toISOString().slice(0, 10)

/* API ประกอบเส้นทางไว้ไม่เกิน 12 ก้าว (เพดาน GROUP_CONCAT) — ต้องตรงกับฝั่ง API */
const MAX_SHOWN_STEPS = 12

/* ความลึกสูงสุดที่ API รับ (MAX_PREFIX_DEPTH ใน report.model.js) */
const MAX_DEPTH = 10

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BFormSelect,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BAlert,
    BSpinner,
    BLink,
  },
  data() {
    const today = new Date()
    /* 7 วันล่าสุดนับรวมวันนี้ จึงลบ 6 (เปลี่ยนจาก 30 วันเมื่อ 21 ก.ย. 2026) */
    const past = new Date(today.getTime() - 6 * 86400000)

    return {
      // explorer
      prefix: [],
      columns: [],
      /* map ของ page_url -> สถิติเวลาที่อยู่บนหน้า โหลดครั้งเดียวต่อช่วงเวลา */
      dwell: {},
      // ตารางประกอบ
      transitions: [],
      paths: [],
      fromPage: '',
      transitionPage: 1,
      transitionHasMore: false,
      transitionTotal: null,
      transitionTotalPages: null,
      transitionLoading: false,
      pathPage: 1,
      pathHasMore: false,
      pathTotal: null,
      pathTotalPages: null,
      pathLoading: false,
      isLoading: false,
      loadError: '',
      partialError: '',
      preset: 7,
      from: iso(past),
      to: iso(today),
      presetOptions: [
        { value: 7, text: '7 วันล่าสุด' },
        { value: 30, text: '30 วันล่าสุด' },
        { value: 90, text: '90 วันล่าสุด' },
        { value: 0, text: 'กำหนดเอง' },
      ],
      transitionFields: [
        { key: 'from_page', label: 'จากหน้า' },
        { key: 'seconds', label: 'อยู่กี่วิ', class: 'text-center' },
        { key: 'to_page', label: 'ไปหน้า' },
        { key: 'moves', label: 'ครั้ง', class: 'text-center' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
      ],
      pathFields: [
        { key: 'path', label: 'เส้นทาง' },
        { key: 'sessions', label: 'ครั้ง', class: 'text-center' },
        { key: 'visitors', label: 'คน', class: 'text-center' },
      ],
    }
  },
  computed: {
    transitionCardTitle() {
      return this.fromPage ? `จาก ${this.fromPage} ไปไหนต่อ` : 'หน้าไหนไปหน้าไหน'
    },
    transitionParams() {
      return {
        page: this.transitionPage,
        per_page: 15,
        ...(this.fromPage ? { page_url: this.fromPage } : {}),
      }
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    pageLabel,
    hasNextPage,
    showPager,
    hasMultiplePages,

    reportGet(type, extra = {}) {
      return analyticsHttp.get('analytics/v1/report', {
        params: {
          // ต่อเวลาท้ายวันเอง ไม่งั้น MySQL อ่าน to เป็นเที่ยงคืนแล้วข้อมูลทั้งวันนั้นหลุด
          from: `${this.from} 00:00:00`,
          to: `${this.to} 23:59:59`,
          type,
          ...extra,
        },
        headers: { Authorization: `Bearer ${apiToken()}` },
      })
    },

    requireToken() {
      if (apiToken()) return true

      this.loadError = 'ไม่พบ token ของ API ใน userData — กรุณาออกจากระบบแล้วเข้าใหม่'

      return false
    },

    /*
     * โหลดทั้งหน้า
     *
     * ⚠️ ห้ามรอ 4 รายงานเรียงกัน
     * แต่ละตัวใช้ 1.7-2.5 วินาที เดิม await dwell ให้เสร็จก่อนแล้วค่อยยิงที่เหลือ
     * ทำให้ต้องรอ ~4.4 วินาทีก่อนเห็นอะไรเลย
     *
     * ตอนนี้ยิงทุกตัวขนานกัน แล้วปลด spinner ทันทีที่ "ต้นไม้" พร้อม (652ms)
     * ไม่รอ dwell (2,011ms) / ตารางล่าง ซึ่งมี spinner ของตัวเองอยู่แล้ว
     *
     * ⚠️ เคยรอ dwell ก่อนเพื่อกันเลขวินาทีโผล่กระตุกตามมา — แลกผิด
     * หลังใส่ covering index ต้นไม้เร็วขึ้นเป็น 652ms แต่ dwell ยังอ่าน payload อยู่จึงช้ากว่า
     * การรอมันจึงทำให้ผู้ใช้จ้องหน้าว่างเพิ่มอีก 1.4 วินาทีเพื่อแลกกับเลขวินาทีที่โผล่ช้าไป
     * นิดเดียว — ให้ต้นไม้ขึ้นก่อนคุ้มกว่ามาก
     */
    async fetchData() {
      if (!this.requireToken()) return

      this.loadError = ''
      this.partialError = ''
      this.prefix = []
      this.columns = []

      // .catch() กัน unhandled rejection ระหว่างที่ยังไม่ได้ await
      const rest = Promise.all([this.fetchTransitions(), this.fetchPaths(), this.loadDwell()])
        .catch(error => { console.error('Error loading user path extras:', error) })

      try {
        this.isLoading = true
        await this.loadColumn(0)
      } catch (error) {
        this.columns = []
        this.loadError = loadErrorMessage(error)
        console.error('Error loading user path:', error)
      } finally {
        this.isLoading = false
      }

      await rest
    },

    /*
     * โหลดคอลัมน์ที่ depth หนึ่ง ๆ โดยใช้ prefix ปัจจุบันตัดที่ depth นั้น
     *
     * ⚠️ กันคำตอบที่มาช้าเขียนทับของใหม่
     * ผู้ใช้กดกางเร็ว ๆ หลายครั้ง คำตอบอาจกลับมาไม่เรียงลำดับ API จึงคืน depth มาด้วย
     * ถ้า depth ที่ได้ไม่ตรงกับที่กำลังรออยู่ ให้ทิ้งคำตอบนั้นไป
     */
    async loadColumn(depth) {
      const prefix = this.prefix.slice(0, depth)

      this.$set(this.columns, depth, { rows: [], total: 0, loading: true })
      this.columns = this.columns.slice(0, depth + 1)

      const r = await settleAll({
        tree: this.reportGet('path_tree', {
          ...(prefix.length ? { prefix: JSON.stringify(prefix) } : {}),
          branches: 10,
        }),
      })

      // ระหว่างรอ ผู้ใช้อาจถอยกลับไปขั้นอื่นแล้ว
      if (JSON.stringify(this.prefix.slice(0, depth)) !== JSON.stringify(prefix)) return

      const rows = rowsOf(r.tree).filter(x => x.depth === depth)

      this.$set(this.columns, depth, {
        rows,
        total: rows.reduce((a, x) => a + Number(x.sessions || 0), 0),
        loading: false,
      })

      const message = partialErrorMessage(r, { tree: 'เส้นทางผู้ใช้' })

      if (message) this.partialError = message
    },

    /* คลิกกล่อง -> ตั้ง prefix ถึงขั้นนั้นแล้วกางขั้นถัดไป */
    onNodeClick(colIndex, node) {
      if (node.kind !== 'page') return Promise.resolve()
      if (colIndex + 1 >= MAX_DEPTH) return Promise.resolve()

      this.prefix = [...this.prefix.slice(0, colIndex), node.next_page]

      // คืน promise ออกไปด้วย เพื่อให้เทสรอผลได้ (Vue ไม่ได้ใช้ค่านี้)
      return this.loadColumn(colIndex + 1)
    },

    /* คลิก breadcrumb -> ถอยกลับไปขั้นนั้น */
    goToDepth(depth) {
      this.prefix = this.prefix.slice(0, depth)

      return this.loadColumn(depth)
    },

    /* สถิติเวลาของหน้าในกล่องนั้น — undefined ถ้าไม่ใช่กล่องหน้า หรือหน้านั้นไม่มีข้อมูล */
    dwellOf(node) {
      if (!node || node.kind === 'exit' || node.kind === 'other') return null

      return this.dwell[node.next_page] || null
    },

    /* 0 วินาทีเกินครึ่ง = SDK จับเวลาไม่ทัน ตัวเลขของหน้านั้นเชื่อไม่ได้ */
    dwellShaky(node) {
      const d = this.dwellOf(node)

      return !!d && Number(d.zero_pct) > 50
    },

    dwellTitle(node) {
      const d = this.dwellOf(node)

      if (!d) return 'ไม่มีข้อมูลเวลาสำหรับหน้านี้'

      const shaky = Number(d.zero_pct) > 50
        ? ` — ระวัง: ${d.zero_pct}% ของครั้งที่เข้ารายงาน 0 วินาที ตัวเลขนี้จึงเชื่อได้ไม่เต็มที่`
        : ''

      return `มัธยฐาน ${d.median_seconds} วิ · p75 ${d.p75_seconds} วิ · เฉลี่ย ${d.avg_seconds} วิ · จาก ${d.samples} ครั้ง${shaky}`
    },

    isOpen(colIndex, node) {
      return node.kind === 'page' && this.prefix[colIndex] === node.next_page
    },

    nodeLabel(node) {
      if (node.kind === 'exit') return 'ออกจากเว็บ'
      if (node.kind === 'other') return `อื่น ๆ (${node.other_pages} หน้า)`

      return this.shorten(node.next_page, 30)
    },
    nodeTitle(node) {
      if (node.kind === 'exit') return 'จบการเข้าชมตรงนี้'
      if (node.kind === 'other') return `หน้าที่เหลือ ${node.other_pages} หน้า รวมกัน (กางต่อไม่ได้)`

      return node.next_page
    },

    /* สัดส่วนเทียบยอดรวมของคอลัมน์นั้น — 0 ถ้ายังไม่มียอด จะได้ไม่หาร 0 */
    pct(node, col) {
      const total = Number(col.total || 0)

      return total ? Math.round((100 * Number(node.sessions || 0)) / total) : 0
    },

    async fetchTransitions() {
      if (!this.requireToken()) return

      try {
        this.transitionLoading = true
        await this.loadTransitions()
      } finally {
        this.transitionLoading = false
      }
    },
    async fetchPaths() {
      if (!this.requireToken()) return

      try {
        this.pathLoading = true
        await this.loadPaths()
      } finally {
        this.pathLoading = false
      }
    },

    /*
     * เวลาที่อยู่บนแต่ละหน้า — ผูกกับช่วงเวลาอย่างเดียว ไม่ขึ้นกับ prefix ที่กางอยู่
     * จึงโหลดครั้งเดียวแล้วใช้ได้ทุกคอลัมน์ ไม่ต้องยิงซ้ำตอนกาง
     */
    async loadDwell() {
      const r = await settleAll({
        dwell: this.reportGet('page_dwell', { per_page: 200 }),
      })

      this.dwell = Object.fromEntries(rowsOf(r.dwell).map(x => [x.page_url, x]))

      const message = partialErrorMessage(r, { dwell: 'เวลาที่อยู่บนหน้า' })

      if (message) this.partialError = message
    },

    async loadTransitions() {
      const r = await settleAll({
        transitions: this.reportGet('page_transitions', this.transitionParams),
      })

      this.transitions = rowsOf(r.transitions)
      this.transitionHasMore = r.transitions.hasMore
      this.transitionTotal = r.transitions.total
      this.transitionTotalPages = r.transitions.totalPages

      const message = partialErrorMessage(r, { transitions: 'หน้าไหนไปหน้าไหน' })

      if (message) this.partialError = message
    },

    async loadPaths() {
      const r = await settleAll({
        paths: this.reportGet('session_paths', { page: this.pathPage, per_page: 15 }),
      })

      this.paths = rowsOf(r.paths)
      this.pathHasMore = r.paths.hasMore
      this.pathTotal = r.paths.total
      this.pathTotalPages = r.paths.totalPages

      const message = partialErrorMessage(r, { paths: 'เส้นทางที่พบบ่อย' })

      if (message) this.partialError = message
    },

    /*
     * @row-clicked ยิงเฉพาะตอนคนกดจริง ต่างจาก @row-selected ที่ b-table ยิงเองด้วย
     * ตอนมันเคลียร์ selection ให้อัตโนมัติเมื่อ items เปลี่ยน
     */
    onTransitionClicked(item) {
      const next = item?.from_page || ''

      this.fromPage = next === this.fromPage ? '' : next
      this.transitionPage = 1
      this.fetchTransitions()
    },
    clearFromPage() {
      this.fromPage = ''
      this.transitionPage = 1
      this.fetchTransitions()
    },
    transitionRowClass(item) {
      return item && this.fromPage && item.from_page === this.fromPage ? 'table-active' : ''
    },
    goTransitionPage(page) {
      this.transitionPage = Math.max(1, page)
      this.fetchTransitions()
    },
    goPathPage(page) {
      this.pathPage = Math.max(1, page)
      this.fetchPaths()
    },

    onPresetChange() {
      if (!this.preset) return

      const today = new Date()

      this.to = iso(today)
      this.from = iso(new Date(today.getTime() - (this.preset - 1) * 86400000))
      this.resetPages()
      this.fetchData()
    },
    onDateChange() {
      this.preset = 0
      this.resetPages()
      this.fetchData()
    },
    resetPages() {
      // เปลี่ยนช่วงเวลาแล้วต้องกลับหน้าแรก ไม่งั้นค้างอยู่หน้าที่อาจไม่มีข้อมูล
      this.transitionPage = 1
      this.pathPage = 1
    },

    /* URL ยาว ๆ (โดยเฉพาะภาษาไทยที่ encode แล้ว) ดันตารางกว้างจนอ่านไม่ไหว */
    shorten(url, max = 46) {
      let text = String(url || '')

      try { text = decodeURI(text) } catch (error) { /* URL ที่ encode มาไม่ถูก — ใช้ค่าดิบ */ }

      return text.length > max ? `${text.slice(0, max)}…` : text
    },

    /* ก้าวที่ API ตัดทิ้งไปเพราะเพดาน GROUP_CONCAT — 0 แปลว่าแสดงครบแล้ว */
    hiddenSteps(item) {
      return Math.max(0, Number(item.steps || 0) - MAX_SHOWN_STEPS)
    },
  },
}
</script>

<style scoped>
/* URL ยาวดันตารางกว้างเกินจำเป็นบนมือถือ */
:deep(.table td) {
  word-break: break-word;
}

:deep(.path-table tbody tr) {
  cursor: pointer;
}

.path-cell {
  line-height: 2.1;
}

/* ── path explorer ────────────────────────────────────────────────────────── */
.path-crumbs {
  font-size: 0.9rem;
}

.crumb {
  color: inherit;
}

.crumb-active {
  font-weight: 600;
}

/*
 * เลื่อนแนวนอนได้ เพราะจำนวนคอลัมน์เพิ่มตามความลึกที่ผู้ใช้กาง
 * ไม่จำกัดความสูง ให้แต่ละคอลัมน์ยาวตามจำนวนกิ่ง
 */
.path-explorer {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
}

.path-col {
  flex: 0 0 15rem;
  min-width: 15rem;
}

.path-col-head {
  font-size: 0.85rem;
  font-weight: 600;
  padding-bottom: 0.35rem;
  margin-bottom: 0.35rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.path-node {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.35rem;
  margin-bottom: 0.35rem;
  background: rgba(0, 0, 0, 0.01);
}

.path-node-clickable {
  cursor: pointer;
}

.path-node-clickable:hover {
  border-color: rgba(115, 103, 240, 0.5);
}

.path-node-open {
  border-color: #7367f0;
  box-shadow: 0 0 0 1px #7367f0 inset;
}

/* แถบสัดส่วนวางเป็นพื้นหลัง ตัวหนังสือลอยอยู่ด้านบน */
.path-node-bar {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: rgba(115, 103, 240, 0.14);
}

.path-node-exit .path-node-bar {
  background: rgba(234, 84, 85, 0.14);
}

.path-node-other .path-node-bar {
  background: rgba(0, 0, 0, 0.06);
}

.path-node-body {
  position: relative;
  padding: 0.4rem 0.55rem;
}

.path-node-label {
  font-size: 0.85rem;
  word-break: break-all;
}

.path-node-meta {
  font-size: 0.8rem;
  font-weight: 600;
}

.path-node-time {
  font-weight: 400;
  color: #7367f0;
}
</style>
